/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  CONFIRMATION: 'confirmation',
  EXECUTION: 'execution',
  EXECUTION_FAILED: 'executionFailed',
  FAILED: 'failed',
  SUCCESS: 'success'
};

const EXECUTE_RESULT_TYPE = {
  OK: "ok",
  NG: "ng"
};

export default {
  name: 'requirement030',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      modal_requirement_030: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.EXECUTION_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.EXECUTION:
        default:
          return 'secondary';
      }
    },

    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      const confirmationTitle = 'message.common_title_confirmation';
      const executionTitle = 'individual.common_title_execution';
      const successTitle = 'message.common_title_success';
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        start: confirmationTitle,
        confirmation: confirmationTitle,
        execution: executionTitle,
        success: successTitle,
        executionFailed: errorTitle,
        failed: errorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * ダイアログの表示文言
     */
    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.REQ_WS_30_TXT_1`,
        confirmation: `${this.i18nWsKey}.REQ_WS_30_TXT_2`,
        execution: `${this.i18nWsKey}.REQ_WS_30_TXT_3_0000`,
        success: `${this.i18nWsKey}.REQ_WS_30_TXT_4_0002`,
        executionFailed: `${this.i18nWsKey}.REQ_WS_30_TXT_5_0010`,
        failed: `individual.error_communication_error`
      };
      return this.$t(messageMap[this.phase]);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_030 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** 待機画面の「OK」ボタン押下 */
    confirmProcess() {
      // 実行画面へ遷移
      this.phase = PHASE.CONFIRMATION;
    },

    /** 実行処理（要求） */
    async execute() {
      try {
        this.isLoading = true;
        this.phase = PHASE.EXECUTION;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;
        // 正常コールバック
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              // 停止画面に遷移する。
              case EXECUTE_RESULT_TYPE.OK:
                this.phase = PHASE.SUCCESS;
                break;
              // エラー画面に遷移する。                
              case EXECUTE_RESULT_TYPE.NG:
                this.phase = PHASE.EXECUTION_FAILED;
                break;
              // それ以外（共通エラー）
              default:
                this.showUnexpectedError();
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 待機画面の「キャンセル」/停止画面の「OK」/エラー画面の「OK」/共通エラー画面の「OK」ボタン押下 */
    async exitProcess() {
      switch (this.phase) {
        // 待機画面の「キャンセル」ボタン押下：機能終了
        case PHASE.START:
          this.closeWizard();
          break;
        // 停止画面の「OK」/エラー画面の「OK」ボタン押下：Exit処理（エラー遷移あり） 
        case PHASE.SUCCESS:
        case PHASE.EXECUTION_FAILED:
          // DefaultSession遷移
          await this.exit(true);
          break;
        // 共通エラー画面の「OK」ボタン押下：Exit処理（エラー遷移なし）
        case PHASE.FAILED:
        default:
          await this.exit(false);
      }
    },

    /** Exit処理（要求）
     * @param {boolean} existence true:エラー遷移あり, false:エラー遷移なし 
     */
    async exit(existence) {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // エラー遷移あり
        if (existence) {
          if (!response) return;
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了：機能終了
              this.closeWizard();
              break;
            case REQUEST_STATUS_FAILED:
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              return;
          }
          // エラー遷移なし  
        } else {
          this.closeWizard();
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * OKボタン押下時の処理
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.START:
          this.confirmProcess();
          break;
        case PHASE.CONFIRMATION:
          await this.execute();
          break;
        case PHASE.SUCCESS:
        case PHASE.EXECUTION_FAILED:
        case PHASE.FAILED:
          await this.exitProcess();
          break;
      }
    },

    /**
     * キャンセルボタン押下時の処理
     */
    async onCancelClicked() {
      await this.exitProcess();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_030 = false;
    },
  },

  mounted() {
    this.init();
  },
};